// frontend/src/pages/UsersPage.jsx
import React, { useState, useEffect } from 'react';
import { getUsers, createUser, deleteUser } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Loader, Shield, User, CheckCircle, XCircle } from 'lucide-react';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to load users', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!form.name || !form.email || !form.password) {
      setError('All fields are required');
      return;
    }

    try {
      await createUser(form);
      setSuccess('User created successfully');
      setShowForm(false);
      setForm({ name: '', email: '', password: '', role: 'user' });
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (id, name) => {
    if (id === currentUser.id) {
      setError('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Are you sure you want to remove ${name}?`)) return;

    try {
      await deleteUser(id);
      setUsers(users.filter((u) => u.id !== id));
      setSuccess('User removed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Users</h1>
          <p className="text-gray-500 mt-1">Manage who can access the system</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid transition-colors"
        >
          <Plus size={18} />
          Add User
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center gap-2">
          <CheckCircle size={18} />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2">
          <XCircle size={18} />
          {error}
        </div>
      )}

      {/* Add User Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-brand-dark mb-4">Add New User</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                placeholder="email@example.com"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                placeholder="Strong password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
              >
                <option value="user">User (can run and view content)</option>
                <option value="admin">Admin (full access)</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid text-sm"
            >
              <Plus size={16} />
              Create User
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-blue-700">
          <strong>Roles:</strong> Admins have full access to all settings, topics, brand voice, and user management.
          Users can trigger runs, view and edit content, but cannot modify system configuration.
        </p>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="divide-y divide-gray-100">
          {users.map((u) => (
            <div key={u.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-full ${u.role === 'admin' ? 'bg-brand-light' : 'bg-gray-100'}`}>
                  {u.role === 'admin' ? (
                    <Shield size={20} className="text-brand-mid" />
                  ) : (
                    <User size={20} className="text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {u.name}
                    {u.id === currentUser.id && (
                      <span className="text-xs text-gray-400 ml-2">(you)</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                  u.role === 'admin' ? 'bg-brand-light text-brand-dark' : 'bg-gray-100 text-gray-600'
                }`}>
                  {u.role}
                </span>
                {u.id !== currentUser.id && (
                  <button
                    onClick={() => handleDelete(u.id, u.name)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove user"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsersPage;